import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import ExpenseEditor from "./ExpenseEditor";
import ProfitCard from "./ProfitCard";

function ToggleChip({ checked, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
        checked ? "bg-gg-green text-white" : "bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green"
      }`}
    >
      {label}
    </button>
  );
}

function FieldLabel({ label, children, className = "" }) {
  return (
    <Label className={`block ${className}`}>
      <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">{label}</span>
      <div className="mt-1.5">{children}</div>
    </Label>
  );
}

export default function BookingEditDialog({ booking, onClose, onSaved }) {
  const [form, setForm] = useState({
    student_name: booking.student_name || "",
    parent_name: booking.parent_name || "",
    parent_email: booking.parent_email || "",
    parent_phone: booking.parent_phone || "",
    esa_contract_number: booking.esa_contract_number || "",
    subject: booking.subject || "",
    client_address: booking.client_address || "",
    allergies_notes: booking.allergies_notes || "",
    admin_notes: booking.admin_notes || "",
    curriculum_delivered: !!booking.curriculum_delivered,
    archived: !!booking.archived,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/bookings/${booking.id}`, form);
      toast.success("Booking updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!confirm(`Permanently DELETE this 1-on-1 booking for ${booking.student_name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/bookings/${booking.id}`);
      toast.success("Booking deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-gg-surface max-h-[90vh] overflow-y-auto" data-testid="booking-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            Edit 1-on-1 booking · {booking.service_label || "Session"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4">
          <FieldLabel label="Student name">
            <Input value={form.student_name} onChange={set("student_name")} data-testid="edit-booking-student" />
          </FieldLabel>
          <FieldLabel label="ESA contract #">
            <Input value={form.esa_contract_number} onChange={set("esa_contract_number")} />
          </FieldLabel>
          <FieldLabel label="Parent name">
            <Input value={form.parent_name} onChange={set("parent_name")} />
          </FieldLabel>
          <FieldLabel label="Parent email">
            <Input type="email" value={form.parent_email} onChange={set("parent_email")} />
          </FieldLabel>
          <FieldLabel label="Parent phone">
            <Input value={form.parent_phone} onChange={set("parent_phone")} />
          </FieldLabel>
          <FieldLabel label="Subject focus">
            <Input value={form.subject} onChange={set("subject")} placeholder="What does the student want to learn?" />
          </FieldLabel>
          {booking.location_type === "athome" && (
            <FieldLabel label="Client address" className="sm:col-span-2">
              <Input value={form.client_address} onChange={set("client_address")} />
            </FieldLabel>
          )}
          <FieldLabel label="⚠ Allergies / accommodations" className="sm:col-span-2">
            <Textarea value={form.allergies_notes} onChange={set("allergies_notes")} rows={2} placeholder="Note any dietary, allergy, or accommodation needs" />
          </FieldLabel>
          <FieldLabel label="📝 Private admin notes" className="sm:col-span-2">
            <Textarea value={form.admin_notes} onChange={set("admin_notes")} rows={3} placeholder="Lesson plan progress, follow-up reminders…" data-testid="edit-booking-notes" />
          </FieldLabel>

          <div className="sm:col-span-2 flex flex-wrap gap-2 pt-1">
            <ToggleChip
              checked={form.curriculum_delivered}
              onClick={() => set("curriculum_delivered")(!form.curriculum_delivered)}
              label={form.curriculum_delivered ? "✓ Curriculum delivered" : "Curriculum pending"}
              testid="edit-booking-curriculum-toggle"
            />
            <ToggleChip
              checked={form.archived}
              onClick={() => set("archived")(!form.archived)}
              label={form.archived ? "✓ Archived" : "Active"}
              testid="edit-booking-archive-toggle"
            />
          </div>

          <div className="sm:col-span-2 pt-2">
            <ProfitCard kind="booking" id={booking.id} />
          </div>
          <div className="sm:col-span-2">
            <ExpenseEditor scope="booking" parentId={booking.id} />
          </div>
        </div>

        <DialogFooter className="mt-4 flex justify-between gap-2">
          <button type="button" onClick={del} disabled={busy} className="btn-pill btn-outline text-[#B24C4C] border-[#B24C4C]/40 hover:bg-[#B24C4C]/10" data-testid="edit-booking-delete">
            Delete booking
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
            <button type="button" onClick={save} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="edit-booking-save">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
